import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
} from "react";

import { useStore } from "zustand/react";
import type { StoreApi } from "zustand/vanilla";

import { useOptionalGroupRegistry } from "../groups/GroupRegistry.context";
import { mirrorGroupRegistryDocument } from "../groups/groupRegistry.mirror";
import { createWorkspaceStore } from "./createWorkspaceStore";
import { withManagedTemplateGeometry } from "./deskEvaluationHydration";
import { useOptionalWorkspaceRepository } from "./WorkspaceRepository.context";
import type { WorkspaceStore } from "./workspace.types";

export type WorkspaceStoreApi = StoreApi<WorkspaceStore>;

const WorkspaceContext = createContext<WorkspaceStoreApi | null>(null);

const WorkspacePersistContext = createContext<{
  persist: boolean;
  workspaceId: string | null;
}>({ persist: false, workspaceId: null });

export interface WorkspaceProviderProps extends PropsWithChildren {
  /**
   * When true, the store hydrates from the RGL localStorage document on create
   * and tab controllers auto-save to the same key. Omit (or false) in unit
   * tests that supply their own empty store.
   */
  persist?: boolean;
  /**
   * Workspace entry id to hydrate / persist when `persist` is true.
   */
  workspaceId?: string;
}

/**
 * Scoped provider that creates one vanilla Zustand workspace store per
 * mounted tab. Unmounting discards all tab-local state; remounting starts
 * from storage when `persist` is true. Only the active layout tab should be
 * wrapped in this provider.
 */
export const WorkspaceProvider = ({
  children,
  persist = false,
  workspaceId,
}: WorkspaceProviderProps) => {
  const repository = useOptionalWorkspaceRepository();
  const groupRegistry = useOptionalGroupRegistry();
  const storeRef = useRef<WorkspaceStoreApi | null>(null);
  const mirroredRegistryFingerprintRef = useRef<string | null>(null);
  if (!storeRef.current) {
    const entry =
      persist && workspaceId
        ? repository?.document.workspaces.find(
            (workspace) => workspace.id === workspaceId,
          )
        : undefined;
    storeRef.current = createWorkspaceStore({
      ...(entry
        ? {
            snapshot: withManagedTemplateGeometry(
              entry.workspace,
              entry.templateId,
            ),
          }
        : {}),
    });
    if (groupRegistry) {
      mirrorGroupRegistryDocument(storeRef.current, groupRegistry.document);
      mirroredRegistryFingerprintRef.current = JSON.stringify(
        groupRegistry.document,
      );
    }
  }
  useEffect(() => {
    const fingerprint = groupRegistry
      ? JSON.stringify(groupRegistry.document)
      : null;
    if (
      groupRegistry &&
      storeRef.current &&
      mirroredRegistryFingerprintRef.current !== fingerprint
    ) {
      mirrorGroupRegistryDocument(storeRef.current, groupRegistry.document);
      mirroredRegistryFingerprintRef.current = fingerprint;
    }
  }, [groupRegistry]);
  return (
    <WorkspacePersistContext.Provider
      value={{ persist, workspaceId: workspaceId ?? null }}
    >
      <WorkspaceContext.Provider value={storeRef.current}>
        {children}
      </WorkspaceContext.Provider>
    </WorkspacePersistContext.Provider>
  );
};

/**
 * Whether the nearest provider has persistence enabled (false in typical
 * unit tests that omit `persist`).
 */
export const useWorkspacePersist = (): boolean =>
  useContext(WorkspacePersistContext).persist;

/**
 * Workspace entry id for the nearest provider when persistence is enabled.
 */
export const useWorkspacePersistId = (): string | null =>
  useContext(WorkspacePersistContext).workspaceId;

/**
 * Returns the raw vanilla store API for the nearest
 * `WorkspaceProvider`. Throws when called outside a provider.
 */
export const useWorkspaceStore = (): WorkspaceStoreApi => {
  const store = useContext(WorkspaceContext);
  if (!store) {
    throw new Error(
      "useWorkspaceStore must be used within a WorkspaceProvider",
    );
  }
  return store;
};

/**
 * Subscribes to a slice of the nearest workspace store.
 * Selectors must return primitives or stable references (use `useShallow`
 * for derived arrays/objects) to satisfy Zustand v5 referential-equality
 * bailout rules.
 */
export const useWorkspace = <T,>(selector: (state: WorkspaceStore) => T): T =>
  useStore(useWorkspaceStore(), selector);
